// services/ServiceProviderSearchService.js
import api from '../api/api';

class ServiceProviderSearchService {
  // ==================== PUBLIC ROUTES ====================

  /**
   * ✅ NEW: Get all providers with pagination and optional filters
   * GET /api/service-providers/search/all-service-providers/
   * 
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 20, max: 100)
   * @param {string} params.query - Search in businessName, name, serviceType, address, description
   * @param {string} params.serviceType - Filter by service type (comma-separated for multiple)
   * @param {string} params.city - Filter by city
   * @param {number} params.minRating - Minimum rating (0-5)
   * @param {boolean} params.isFeatured - Filter featured providers
   * @param {string} params.accountStatus - Filter by user account status (active, suspended, deleted)
   * @param {string} params.sortBy - Sort field (rating, createdAt, hourlyRate, totalReviews, businessName, name)
   * @param {string} params.sortOrder - Sort order (asc, desc)
   * @param {boolean} params.includeInactive - Include inactive providers (default: false)
   */
  async getAllProviders(params = {}) {
    try {
      const {
        page = 1,
        limit = 20,
        sortBy = 'createdAt',
        sortOrder = 'desc',
        includeInactive = false,
        query,
        serviceType,
        city,
        minRating,
        isFeatured,
        accountStatus,
        ...filters
      } = params;

      // Build query parameters
      const queryParams = {
        page,
        limit,
        sortBy,
        sortOrder,
        includeInactive
      };

      // Add optional filters
      if (query) queryParams.query = query;
      if (serviceType) queryParams.serviceType = serviceType;
      if (city) queryParams.city = city;
      if (minRating) queryParams.minRating = minRating;
      if (isFeatured !== undefined && isFeatured !== null) queryParams.isFeatured = isFeatured;
      if (accountStatus) queryParams.accountStatus = accountStatus;

      // Add additional filters
      Object.keys(filters).forEach(key => {
        if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
          queryParams[key] = filters[key];
        }
      });

      const response = await api.get('/all-service-providers/', { 
        params: queryParams 
      });
      
      console.log('✅ All providers loaded:', response.data);
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Advanced search with multiple filters
   * POST /api/service-providers/search/advanced
   */
  async advancedSearch(searchParams) {
    try {
      const response = await api.post('/service-providers/search/advanced', searchParams);
      console.log('provider=>', JSON.stringify(response.data.data))
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  } 

  /**
   * Search nearby providers
   * GET /api/service-providers/search/nearby
   */
  async searchNearby(lat, lng, radius = 10, filters = {}) {
    try {
      const params = {
        lat,
        lng,
        radius,
        ...filters
      };
      const response = await api.get('/service-providers/search/nearby', { params });
      console.log('provider=>', JSON.stringify(response.data))
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get nearby providers worldwide with automatic location detection
   * GET /api/service-providers/search/nearby-worldwide
   */
  async getNearbyProviders(radius = 10, filters = {}) {
    try {
      const params = {
        radius,
        ...filters
      };
      const response = await api.get('/service-providers/search/nearby-worldwide', { params });
      console.log('provider=>', JSON.stringify(response.data.data))
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Autocomplete search for service types, cities, or names
   * GET /api/service-providers/search/autocomplete
   */
  async autocomplete(searchTerm, type = 'service') {
    try {
      const response = await api.get('/service-providers/search/autocomplete', {
        params: { q: searchTerm, type }
      });
      console.log('provider=>', JSON.stringify(response.data.data))
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get search filters (available options for filtering)
   * GET /api/service-providers/search/filters
   */
  async getSearchFilters() {
    try {
      const response = await api.get('/service-providers/search/filters');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Search by availability (for a specific day and time)
   * GET /api/service-providers/search/availability
   */
  async searchByAvailability(day, time, filters = {}) {
    try {
      const params = {
        day,
        time,
        ...filters
      };
      const response = await api.get('/service-providers/search/availability', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get popular providers
   * GET /api/service-providers/search/popular
   */
  async getPopularProviders(limit = 10, filters = {}) {
    try {
      const params = {
        limit,
        ...filters
      };
      const response = await api.get('/service-providers/search/popular', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by rating range
   * GET /api/service-providers/search/rating-range
   */
  async getByRatingRange(minRating, maxRating = 5, page = 1, limit = 20) {
    try {
      const response = await api.get('/service-providers/search/rating-range', {
        params: { minRating, maxRating, page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by price range
   * GET /api/service-providers/search/price-range
   */
  async getByPriceRange(minPrice, maxPrice, page = 1, limit = 20) {
    try {
      const response = await api.get('/service-providers/search/price-range', {
        params: { minPrice, maxPrice, page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Fuzzy search providers
   * GET /api/service-providers/search/fuzzy
   */
  async fuzzySearch(query, options = {}) {
    try {
      const params = {
        q: query,
        ...options
      };
      const response = await api.get('/service-providers/search/fuzzy', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get search statistics
   * GET /api/service-providers/search/statistics
   */
  async getSearchStatistics() {
    try {
      const response = await api.get('/service-providers/search/statistics');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get providers by service type (shortcut)
   * GET /api/service-providers/search/type/:serviceType
   */
  async getByServiceType(serviceType, page = 1, limit = 20) {
    try {
      const response = await api.get(`/service-providers/search/type/${serviceType}`, {
        params: { page, limit }
      });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== RANDOM PROVIDER ROUTES ====================

  /**
   * Get random providers
   * GET /api/service-providers/search/random
   */
  async getRandomProviders(limit = 10, lat = null, lng = null, unit = 'miles') {
    try {
      const params = { limit };
      if (lat && lng) {
        params.lat = lat;
        params.lng = lng;
        params.unit = unit;
      }
      const response = await api.get('/service-providers/search/random', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get random providers by service type
   * GET /api/service-providers/search/random/:serviceType
   */
  async getRandomProvidersByServiceType(serviceType, limit = 10) {
    try {
      const params = { limit };
      const response = await api.get(`/service-providers/search/random/${serviceType}`, { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get random featured providers
   * GET /api/service-providers/search/random-featured
   */
  async getRandomFeaturedProviders(limit = 5) {
    try {
      const params = { limit };
      const response = await api.get('/service-providers/search/random-featured', { params });
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== PROTECTED ROUTES ====================

  /**
   * Clear search cache (Admin only)
   * DELETE /api/service-providers/search/cache
   */
  async clearCache() {
    try {
      const response = await api.delete('/service-providers/search/cache');
      return response.data;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  // ==================== HELPER METHODS ====================

  /**
   * Handle API errors
   */
  handleError(error) {
    let errorMessage = ' ';
    
    if (error.response?.data?.data?.error?.message) {
      errorMessage = error.response.data.data.error.message;
    } else if (error.response?.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    return {
      success: false,
      error: errorMessage,
      statusCode: error.response?.status,
      data: error.response?.data
    };
  }

  /**
   * Get service type options - Complete list
   */
  getServiceTypes() {
    return [
      // Trades & Construction
      'Electrician', 'Plumber', 'Mason', 'Painter', 'Carpenter', 
      'Tiler', 'Flooring Specialist', 'Handyman', 'HVAC Technician',
      'Solar Installer',
      // Beauty & Personal Care
      'Barber', 'Hairdresser', 'Makeup Artist', 'Nail Technician',
      'Spa & Massage Therapist',
      // Automotive
      'Mechanic', 'Auto Body Repair', 'Car Wash & Detailing',
      // Home Services
      'Interior Designer', 'Landscaper', 'Cleaner', 'Pest Control',
      'Pool Maintenance', 'Locksmith', 'Appliance Repair',
      // Food & Beverage Services
      'Restaurant', 'Catering', 'Food Delivery', 'Food Truck',
      'Bakery', 'Pastry Shop', 'Coffee Shop', 'Juice Bar',
      'Ice Cream Parlor', 'Food Caterer', 'Meal Prep Service',
      'Culinary Classes',
      // Other Services
      'Photographer', 'Videographer', 'Event Planner', 'Wedding Planner',
      'Party Rentals', 'DJ & Entertainment', 'Tutor', 'Language Instructor',
      'Fitness Trainer', 'Yoga Instructor', 'Childcare', 'Elder Care',
      'Pet Grooming', 'Pet Sitting', 'Laundry Service', 'Tailor',
      'Shoe Repair', 'Jewelry Repair', 'IT Support', 'Web Developer',
      'Graphic Designer', 'Digital Marketing', 'Consultant',
      'Notary Public', 'Translation Services', 'Tour Guide',
      'Transportation', 'Delivery Service', 'Moving Company',
      'Storage Services', 'Other'
    ];
  }

  /**
   * Get language options
   */
  getLanguageOptions() {
    return ['Creole', 'French', 'English', 'Spanish'];
  }

  /**
   * Get day options
   */
  getDayOptions() {
    return ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  }

  /**
   * Get max radius for search (in miles)
   */
  getMaxRadiusMiles() {
    return 31; // ~50km
  }

  /**
   * Get default radius for search (in miles)
   */
  getDefaultRadiusMiles() {
    return 6.2; // ~10km
  }

  /**
   * Validate coordinates
   */
  validateCoordinates(lat, lng) {
    if (!lat || !lng) {
      return { valid: false, error: 'Latitude and longitude are required' };
    }

    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);

    if (isNaN(latNum) || isNaN(lngNum)) {
      return { valid: false, error: 'Invalid coordinates format' };
    }

    if (latNum < -90 || latNum > 90) {
      return { valid: false, error: 'Latitude must be between -90 and 90' };
    }

    if (lngNum < -180 || lngNum > 180) {
      return { valid: false, error: 'Longitude must be between -180 and 180' };
    }

    return { valid: true, latitude: latNum, longitude: lngNum };
  }

  /**
   * Build search query from filters
   */
  buildSearchQuery(filters) {
    const query = {};

    if (filters.query) {
      query.query = filters.query;
    }

    if (filters.serviceType) {
      query.serviceType = filters.serviceType;
    }

    if (filters.city) {
      query.city = filters.city;
    }

    if (filters.minRating) {
      query.minRating = parseFloat(filters.minRating);
    }

    if (filters.minPrice) {
      query.minPrice = parseFloat(filters.minPrice);
    }

    if (filters.maxPrice) {
      query.maxPrice = parseFloat(filters.maxPrice);
    }

    if (filters.isFeatured !== undefined) {
      query.isFeatured = filters.isFeatured;
    }

    if (filters.languages) {
      query.languages = Array.isArray(filters.languages) ? filters.languages : [filters.languages];
    }

    if (filters.availability) {
      query.availability = filters.availability;
    }

    if (filters.sortBy) {
      query.sortBy = filters.sortBy;
    }

    if (filters.sortOrder) {
      query.sortOrder = filters.sortOrder;
    }

    if (filters.includeInactive !== undefined) {
      query.includeInactive = filters.includeInactive;
    }

    if (filters.near) {
      query.near = filters.near;
    }

    if (filters.unit) {
      query.unit = filters.unit;
    }

    if (filters.accountStatus) {
      query.accountStatus = filters.accountStatus;
    }

    return query;
  }

  /**
   * Format provider for display
   */
  formatProvider(provider) {
    if (!provider) return null;

    return {
      id: provider._id || provider.id,
      name: provider.name,
      businessName: provider.businessName,
      serviceType: provider.serviceType,
      customServiceType: provider.customServiceType,
      rating: provider.rating || 0,
      totalReviews: provider.totalReviews || 0,
      hourlyRate: provider.hourlyRate || 0,
      location: provider.location,
      address: provider.address,
      profileImage: provider.profileImage,
      isFeatured: provider.isFeatured || false,
      languages: provider.languages || [],
      availability: provider.availability,
      distance: provider.distance,
      distanceFormatted: provider.distanceFormatted,
      distanceInKm: provider.distanceInKm,
      relevanceScore: provider.relevanceScore,
      description: provider.description,
      totalJobsCompleted: provider.totalJobsCompleted || 0,
      responseTime: provider.responseTime,
      // User data
      user: provider.user,
      accountStatus: provider.accountStatus,
      hasLocation: provider.hasLocation,
      fullAddress: provider.fullAddress
    };
  }

  /**
   * Format search results
   */
  formatSearchResults(response) {
    if (!response?.data) {
      return {
        providers: [],
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      };
    }

    const data = response.data;
    const providers = Array.isArray(data) ? data : (data.providers || data.results || []);

    return {
      providers: providers.map(p => this.formatProvider(p)),
      total: data.total || providers.length,
      page: data.page || 1,
      limit: data.limit || 20,
      totalPages: data.totalPages || Math.ceil((data.total || providers.length) / (data.limit || 20)),
      hasNext: data.hasNext || false,
      hasPrev: data.hasPrev || false,
      filters: data.filters || {}
    };
  }

  /**
   * Get search cache TTL
   */
  getCacheTTL() {
    return 5 * 60 * 1000; // 5 minutes
  }
}

export default new ServiceProviderSearchService();